import { useEffect, useRef } from 'react';

function getFirstNonTextNodeAdded(mutation: MutationRecord): Element | undefined {
  for (let i = 0; i < mutation.addedNodes.length; i += 1) {
    const addedNode = mutation.addedNodes[i];
    if (addedNode instanceof Element) {
      return addedNode;
    }
  }
  return undefined;
}

/**
 * Uses a MutationObserver to scroll newly added content into view
 * (e.g. a loading indicator, an error message or a download link).
 * Same pattern as ModalMutationObserver, but for non-scrollable containers.
 */
export function useScrollIntoViewOnMutation<T extends HTMLElement>() {
  const reference = useRef<T>(null);

  useEffect(() => {
    const element = reference.current;
    if (!element) {
      return;
    }

    const mutationObserver = new MutationObserver(mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type !== 'childList') {
          continue;
        }
        const addedNode = getFirstNonTextNodeAdded(mutation);
        if (addedNode) {
          addedNode.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          break;
        }
      }
    });
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return reference;
}
